import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useMcp } from '../../../hooks/useMcp';
import { detalheDoErro, formatarDataHora } from '../../mcp/mcpUtils';
import { BlocoMono } from '../../mcp/ui';
import { AZUL, AZUL_ESCURO, Etiqueta, Eyebrow, Interruptor, TEXTO_SUAVE } from '../ui';

const MINIMO = 12;

const campo = {
  h: '40px',
  borderRadius: '10px',
  bg: 'white',
  borderColor: '#CBD5E1',
  fontSize: '14px',
} as const;

/**
 * Campo MCP do painel do usuário na Central de Permissões: a SENHA DO MCP deste usuário
 * (login 2). Não é a senha do sistema: vale só no MCP e libera as ferramentas que as
 * permissões dele no sistema já permitem. Só o administrador chega aqui.
 */
export function UsuarioMcp({ usuarioId, nome }: { usuarioId: number; nome: string }) {
  const { getAcessoUsuario, definirSenhaUsuario, ativarAcessoUsuario, removerAcessoUsuario } =
    useMcp();
  const cliente = useQueryClient();
  const [senha, setSenha] = useState('');
  const [gerada, setGerada] = useState<string | null>(null);
  const [confirmaRemover, setConfirmaRemover] = useState(false);

  const chave = ['mcp', 'usuario', usuarioId];
  const { data: acesso, isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => getAcessoUsuario(usuarioId),
    refetchOnWindowFocus: false,
  });

  // trocar de usuário limpa o que sobrou na tela (senha digitada ou gerada)
  useEffect(() => {
    setSenha('');
    setGerada(null);
    setConfirmaRemover(false);
  }, [usuarioId]);

  useEffect(() => {
    if (!confirmaRemover) return undefined;
    const t = setTimeout(() => setConfirmaRemover(false), 6000);
    return () => clearTimeout(t);
  }, [confirmaRemover]);

  const atualizar = () => cliente.invalidateQueries({ queryKey: chave });
  const erro = (e: unknown) => toast.error(detalheDoErro(e) ?? 'Não foi possível salvar.');

  const definir = useMutation({
    mutationFn: (corpo: { senha?: string; gerar?: boolean }) =>
      definirSenhaUsuario(usuarioId, corpo),
    onSuccess: resposta => {
      setSenha('');
      setGerada(resposta.gerada);
      toast.success(resposta.gerada ? 'Senha do MCP gerada.' : 'Senha do MCP definida.');
      atualizar();
    },
    onError: erro,
  });
  const alternar = useMutation({
    mutationFn: (ativo: boolean) => ativarAcessoUsuario(usuarioId, ativo),
    onSuccess: () => atualizar(),
    onError: erro,
  });
  const remover = useMutation({
    mutationFn: () => removerAcessoUsuario(usuarioId),
    onSuccess: () => {
      setGerada(null);
      setConfirmaRemover(false);
      toast.success('Senha do MCP removida.');
      atualizar();
    },
    onError: erro,
  });

  if (isLoading || !acesso) return <Spinner size="sm" />;

  return (
    <Flex direction="column" gap="12px">
      <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
        <Eyebrow>MCP</Eyebrow>
        <Flex align="center" gap="10px">
          {!acesso.configurada ? (
            <Etiqueta tom="neutro">sem senha do MCP</Etiqueta>
          ) : (
            <>
              <Etiqueta tom={acesso.ativo ? 'sucesso' : 'aviso'}>
                {acesso.ativo ? 'acesso ativo' : 'acesso desativado'}
              </Etiqueta>
              <Interruptor
                ligado={acesso.ativo}
                desabilitado={alternar.isPending}
                onChange={() => alternar.mutate(!acesso.ativo)}
                rotulo="Acesso ao MCP ativo"
              />
            </>
          )}
        </Flex>
      </Flex>

      <Text fontSize="13px" color="#475569">
        Senha só do MCP (não é a do sistema). A IA entra como <b>{nome}</b> com as permissões dele.
      </Text>

      {gerada && (
        <Flex
          direction="column"
          gap="8px"
          p="12px 14px"
          borderRadius="16px"
          bg="rgba(220,252,231,.7)"
          boxShadow="inset 0 0 0 1px #A7F3D0"
        >
          <Text fontSize="13px" fontWeight={700} color="#166534">
            Senha gerada. Copie agora: ela não será mostrada de novo.
          </Text>
          <BlocoMono copiar>{gerada}</BlocoMono>
          <Flex>
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="white"
              onClick={() => setGerada(null)}
            >
              Já copiei
            </Button>
          </Flex>
        </Flex>
      )}

      <Flex gap="8px" wrap="wrap" align="center">
        <Input
          type="password"
          maxW="300px"
          autoComplete="new-password"
          placeholder={`Nova senha do MCP (mín. ${MINIMO} caracteres)`}
          value={senha}
          onChange={e => setSenha(e.target.value)}
          {...campo}
        />
        <Button
          size="sm"
          h="40px"
          borderRadius="999px"
          color="white"
          bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
          _hover={{ bg: AZUL_ESCURO }}
          isDisabled={senha.length < MINIMO}
          isLoading={definir.isPending}
          onClick={() => definir.mutate({ senha })}
        >
          {acesso.configurada ? 'Trocar senha' : 'Definir senha'}
        </Button>
        <Button
          size="sm"
          h="40px"
          variant="outline"
          borderRadius="999px"
          bg="white"
          isLoading={definir.isPending}
          onClick={() => definir.mutate({ gerar: true })}
        >
          Gerar senha
        </Button>
      </Flex>

      {acesso.configurada && (
        <Flex gap="10px" wrap="wrap" align="center">
          {confirmaRemover ? (
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="white"
              color="#991B1B"
              borderColor="#FCA5A5"
              _hover={{ bg: '#FEE2E2' }}
              isLoading={remover.isPending}
              onClick={() => remover.mutate()}
            >
              Confirmar: remover a senha do MCP
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="white"
              onClick={() => setConfirmaRemover(true)}
            >
              Remover senha
            </Button>
          )}
          <Box flex="1 1 240px">
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              Definida em {formatarDataHora(acesso.dt_definicao)} · último uso{' '}
              {formatarDataHora(acesso.ultimo_uso)}
              {acesso.sessoes_com_o_usuario > 0
                ? ` · autenticado em ${acesso.sessoes_com_o_usuario} sessão(ões) agora`
                : ''}
            </Text>
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
